import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Reharm Lab v1 (Phase 2 Tier 2) lexicon entries — five Bowie-locked
 * substitution-technique entries + one SPEC-authored empty-state entry.
 * Authored by Bowie in the Reharm Lab scope memo; locked verbatim — do
 * not edit without Bowie review.
 *
 * Spec: docs/superpowers/specs/2026-05-19-reharm-lab-design.md §11
 * Bowie scope memo: .claude/agent-memory/bowie/project_reharm_lab_v1_scope.md
 */
export const REHARM_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'subst-tritone-sub',
    category: 'substitution',
    displayName: 'Tritone substitution',
    displayNameShort: 'Tritone sub',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'A jazzy bass-line trick — replace the tension chord with one a tritone away.',
      intermediate: 'Tritone sub — V7 replaced with the dominant 7 a tritone away. Same tension, chromatic bass into tonic.',
      advanced:
        'Tritone substitution — V7 and ♭II7 share the same tritone (3rd ↔ ♭7th); substituting preserves dominant tension while reframing bass motion as chromatic step.',
    },
    tagLabel: { beginner: 'jazz sub', intermediate: 'tritone sub', advanced: '♭II7 for V7' },
  },
  {
    id: 'subst-secondary-dominant',
    category: 'substitution',
    displayName: 'Secondary dominant',
    displayNameShort: 'V7/X',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Insert a V7 chord just before any major or minor chord — pulls into it like a mini key change.',
      intermediate: 'Secondary dominant — V7 of the next chord, inserted to create a momentary tonicization.',
      advanced:
        "Secondary dominant insertion (V7/X) — borrows V7→I motion from the target chord's key for one bar; chromatic third in the inserted chord, resolution by descending fifth.",
    },
    tagLabel: { beginner: 'mini key change', intermediate: 'V7/X', advanced: 'V7-of-target' },
  },
  {
    id: 'subst-borrowed-iv',
    category: 'substitution',
    displayName: 'Borrowed iv (modal interchange)',
    displayNameShort: 'Borrowed iv',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Make the IV chord minor — adds a wistful, parallel-minor color.',
      intermediate: 'Borrowed iv — swap major IV for minor iv from the parallel minor. Same root, ♭3 instead of ♮3.',
      advanced:
        'Modal interchange: parallel-minor iv borrowed into major. Introduces ♭6 (the minor third of iv) — predominant function preserved, mode color shifted.',
    },
    tagLabel: { beginner: 'minor IV', intermediate: 'borrowed iv', advanced: 'iv from parallel minor' },
  },
  {
    id: 'subst-borrowed-flat-VII',
    category: 'substitution',
    displayName: 'Borrowed ♭VII (rock cadence)',
    displayNameShort: 'Borrowed ♭VII',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Swap the tense V chord for ♭VII — the classic rock and folk move.',
      intermediate: 'Borrowed ♭VII — swap V for ♭VII (parallel-minor major chord). Trades functional dominant for Mixolydian subtonic.',
      advanced:
        'Modal interchange: parallel-minor ♭VII as predominant→tonic alternative to V→I. The ♭7 scale degree (Mixolydian color) replaces the leading tone; cadence softens from leading-tone pull to whole-step descent.',
    },
    tagLabel: { beginner: 'rock cadence', intermediate: 'borrowed ♭VII', advanced: '♭VII for V' },
  },
  {
    id: 'subst-deceptive-resolution',
    category: 'substitution',
    displayName: 'Deceptive resolution',
    displayNameShort: 'V→vi',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Instead of landing on home, land on a bittersweet chord — keeps the phrase going.',
      intermediate: 'Deceptive resolution — V resolves to vi instead of I. Same pull, different landing.',
      advanced:
        'Deceptive cadence: V→vi. The vi chord shares two tones with I (root of vi = 3rd of I, 3rd of vi = 5th of I) — the resolution feels almost-home, then pivots.',
    },
    tagLabel: { beginner: 'unexpected landing', intermediate: 'V → vi', advanced: 'deceptive cadence' },
  },
  {
    id: 'subst-none-apply',
    category: 'substitution',
    displayName: 'No substitutions at this slot',
    displayNameShort: 'No subs',
    pedagogicalLevel: 1,
    copy: {
      beginner: "This chord doesn't have substitutions yet — try clicking a V, IV, or V7 chord.",
      intermediate: 'No v1 substitutions apply to this chord role.',
      advanced:
        'No v1 technique predicate matches at this slot. v1 covers V (tritone, ♭VII, deceptive), IV (borrowed iv), and major/minor targets (secondary dominant).',
    },
    tagLabel: { beginner: 'no subs', intermediate: 'no subs', advanced: 'no subs' },
  },
]
